/* ============================================================
   ProtoClinical: global.js
   Dependency-free progressive enhancements that must run on every
   page (incl. password): prefetch-on-intent, predictive search,
   and the country/language disclosure. Keeps navigation feeling
   instant without blocking anything.
   ============================================================ */
(function () {
  "use strict";
  var CFG = window.PC_CONFIG || {};
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));

  /* ---- Prefetch on hover / intent (instant.page style) ---- */
  (function prefetch() {
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ""))) return;
    const seen = new Set();
    let timer = null;
    function add(url) {
      if (!url || seen.has(url)) return;
      try {
        const u = new URL(url, location.href);
        if (u.origin !== location.origin) return;
        if (u.pathname === location.pathname && u.search === location.search) return;
      } catch (e) { return; }
      seen.add(url);
      const l = document.createElement("link");
      l.rel = "prefetch"; l.href = url; l.as = "document";
      document.head.appendChild(l);
    }
    function candidate(e) {
      const a = e.target.closest("a[href], [data-prefetch][href]");
      if (!a) return null;
      const href = a.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return null;
      if (a.hasAttribute("data-no-prefetch")) return null;
      return a.href;
    }
    document.addEventListener("mouseover", (e) => {
      const url = candidate(e); if (!url) return;
      clearTimeout(timer); timer = setTimeout(() => add(url), 65);
    });
    document.addEventListener("mouseout", () => clearTimeout(timer));
    document.addEventListener("touchstart", (e) => { const url = candidate(e); if (url) add(url); }, { passive: true });
  })();

  /* ---- Predictive search ---- */
  (function predictive() {
    const input = $("[data-predictive-search-input]");
    const results = $("[data-predictive-search-results]");
    if (!input || !results) return;
    const base = (CFG.routes && CFG.routes.predictiveSearch) || "/search/suggest";
    let timer = null, controller = null;
    const trendingHTML = results.innerHTML;

    function escapeHTML(value) {
      return String(value == null ? "" : value).replace(/[&<>'"]/g, (char) => ({
        "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;"
      })[char]);
    }
    function imageURL(image) {
      if (!image || !image.url) return "";
      try {
        const url = new URL(image.url, location.origin);
        url.searchParams.set("width", "80");
        return url.href;
      } catch (e) { return image.url; }
    }
    function money(amount) {
      const value = Number(amount);
      if (!Number.isFinite(value)) return "";
      const currency = CFG.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD";
      const locale = document.documentElement.lang || undefined;
      try { return new Intl.NumberFormat(locale, { style: "currency", currency }).format(value); }
      catch (e) { return `${value.toFixed(2)} ${currency}`; }
    }
    function resultImage(resource) {
      const src = imageURL(resource.featured_image);
      if (!src) return '<span class="sg-thumb ph" aria-hidden="true"></span>';
      return `<span class="sg-thumb ph"><img src="${escapeHTML(src)}" alt="" width="40" height="40" loading="lazy"></span>`;
    }
    function render(payload) {
      const products = (payload && payload.products) || [];
      const collections = (payload && payload.collections) || [];
      if (!products.length && !collections.length) {
        results.innerHTML = `<h5>${escapeHTML(results.dataset.noResults || "No matches")}</h5>`;
        return;
      }

      let html = "";
      if (products.length) {
        html += `<h5>${escapeHTML(results.dataset.productsLabel || "Products")}</h5>`;
        html += products.map((product) =>
          `<a href="${escapeHTML(product.url)}" data-prefetch>${resultImage(product)}<span class="sg-name">${escapeHTML(product.title)}</span><span class="sg-price mono">${money(product.price)}</span></a>`
        ).join("");
      }
      if (collections.length) {
        html += `<h5 class="sg-section-title">${escapeHTML(results.dataset.collectionsLabel || "Collections")}</h5>`;
        html += collections.map((collection) =>
          `<a href="${escapeHTML(collection.url)}" class="sg-collection" data-prefetch>${resultImage(collection)}<span class="sg-name">${escapeHTML(collection.title)}</span></a>`
        ).join("");
      }
      results.innerHTML = html;
    }
    function search(q) {
      if (controller) controller.abort();
      controller = new AbortController();
      const request = controller;
      const url = `${base}.json?q=${encodeURIComponent(q)}&resources[type]=product,collection&resources[limit]=4&resources[limit_scope]=each&resources[options][unavailable_products]=last`;
      fetch(url, { signal: controller.signal })
        .then((r) => { if (!r.ok) throw new Error(`Predictive search failed: ${r.status}`); return r.json(); })
        .then((data) => render(data.resources && data.resources.results))
        .catch((error) => { if (error.name !== "AbortError") render(null); })
        .finally(() => { if (controller === request) results.removeAttribute("aria-busy"); });
    }
    input.addEventListener("input", () => {
      const q = input.value.trim();
      clearTimeout(timer);
      if (controller) { controller.abort(); controller = null; }
      if (q.length < 2) {
        results.removeAttribute("aria-busy");
        results.innerHTML = trendingHTML;
        return;
      }
      results.setAttribute("aria-busy", "true");
      timer = setTimeout(() => search(q), 220);
    });
  })();

  /* ---- Country / language disclosure ---- */
  (function disclosure() {
    $$("[data-disclosure]").forEach((d) => {
      const btn = $(".loc-select__btn", d);
      const list = $(".loc-list", d);
      if (!btn || !list) return;
      const close = () => { list.hidden = true; btn.setAttribute("aria-expanded", "false"); };
      const open = () => { list.hidden = false; btn.setAttribute("aria-expanded", "true"); };
      btn.addEventListener("click", (e) => { e.stopPropagation(); list.hidden ? open() : close(); });
      document.addEventListener("click", (e) => { if (!d.contains(e.target)) close(); });
      document.addEventListener("keydown", (e) => { if (e.key === "Escape") close(); });
    });
  })();
})();
